import React, { useState, useEffect } from 'react';
import { Mail, Save, Loader2, CheckCircle2, XCircle, Send, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SmtpSettings = ({ token, toast }) => {
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'SENDBOX',
    smtp_use_tls: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/smtp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.smtp_host) {
        setConfig(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Error loading SMTP settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/smtp`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Succès', description: 'Configuration SMTP enregistrée' });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la sauvegarde',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast({ title: 'Erreur', description: 'Entrez un email de test', variant: 'destructive' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/settings/smtp/test`, { test_email: testEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestResult({ success: true, message: res.data.message || 'Email de test envoyé' });
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Erreur lors du test'
      });
    } finally {
      setTesting(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smtp-settings">
      <div className="bg-slate-800/40 border border-blue-500/20 rounded-xl p-6 space-y-4">
        <h4 className="text-white font-semibold flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-400" />
          Serveur SMTP
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-blue-300/70 mb-1.5">Hôte SMTP *</label>
            <input
              type="text"
              value={config.smtp_host}
              onChange={(e) => updateConfig('smtp_host', e.target.value)}
              placeholder="smtp.gmail.com"
              data-testid="smtp-host"
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-blue-300/70 mb-1.5">Port *</label>
            <input
              type="number"
              value={config.smtp_port}
              onChange={(e) => updateConfig('smtp_port', parseInt(e.target.value) || 587)}
              placeholder="587"
              data-testid="smtp-port"
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-blue-300/70 mb-1.5">Utilisateur SMTP *</label>
            <input
              type="text"
              value={config.smtp_user}
              onChange={(e) => updateConfig('smtp_user', e.target.value)}
              placeholder="user@gmail.com"
              data-testid="smtp-user"
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-blue-300/70 mb-1.5">Mot de passe SMTP *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={config.smtp_password}
                onChange={(e) => updateConfig('smtp_password', e.target.value)}
                placeholder="********"
                data-testid="smtp-password"
                className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300/50 hover:text-blue-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-blue-300/70 mb-1.5">Email expéditeur *</label>
            <input
              type="email"
              value={config.smtp_from_email}
              onChange={(e) => updateConfig('smtp_from_email', e.target.value)}
              placeholder="noreply@sendbox.fr"
              data-testid="smtp-from-email"
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm text-blue-300/70 mb-1.5">Nom expéditeur</label>
            <input
              type="text"
              value={config.smtp_from_name}
              onChange={(e) => updateConfig('smtp_from_name', e.target.value)}
              placeholder="SENDBOX"
              data-testid="smtp-from-name"
              className="w-full bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <input
            type="checkbox"
            checked={config.smtp_use_tls}
            onChange={(e) => updateConfig('smtp_use_tls', e.target.checked)}
            id="smtp-tls"
            className="rounded border-blue-500/30"
          />
          <label htmlFor="smtp-tls" className="text-sm text-blue-300/70">Utiliser TLS/STARTTLS</label>
        </div>
      </div>

      {/* Test SMTP */}
      <div className="bg-slate-800/40 border border-blue-500/20 rounded-xl p-6">
        <h4 className="text-white font-semibold mb-3">Tester la configuration</h4>
        <div className="flex gap-3">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            data-testid="smtp-test-email"
            className="flex-1 bg-slate-900/50 border border-blue-500/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/30 focus:outline-none focus:border-blue-400 text-sm"
          />
          <Button
            onClick={handleTest}
            disabled={testing}
            variant="outline"
            className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
            data-testid="smtp-test-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Envoyer un test
          </Button>
        </div>

        {testResult && (
          <div className={`mt-3 flex items-center gap-2 p-3 rounded-lg ${
            testResult.success ? 'bg-green-500/10 text-green-300' : 'bg-red-500/10 text-red-300'
          }`}>
            {testResult.success ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span className="text-sm">{testResult.message}</span>
          </div>
        )}
      </div>

      {/* Save */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="bg-blue-600 hover:bg-blue-700 text-white"
        data-testid="smtp-save-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Enregistrer
      </Button>
    </div>
  );
};

export default SmtpSettings;
